const tabsState = window.__williesTabsState || (window.__williesTabsState = {
  registry: new WeakMap(),
  bootstrapped: false,
});

class Tabs {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.navLinks = Array.from(wrapper.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(wrapper.querySelectorAll('[role="tabpanel"]'));
    this.navigation = wrapper.querySelector('.tabs-navigation-wrapper') || wrapper;
    this.boundClick = this.onClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);

    this.cleanEmptyParagraphs();
    this.attachEvents();
    this.setInitialActive();
  }

  attachEvents() {
    this.navLinks.forEach((link) => {
      link.addEventListener('click', this.boundClick, true);
      link.addEventListener('keydown', this.boundKeydown, true);
    });
  }

  detachEvents() {
    this.navLinks.forEach((link) => {
      link.removeEventListener('click', this.boundClick, true);
      link.removeEventListener('keydown', this.boundKeydown, true);
    });
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const index = this.navLinks.indexOf(event.currentTarget);
    if (index >= 0) {
      this.setActive(index, { focus: false, toggle: true, keepVisible: true });
    }
  }

  onKeydown(event) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    const currentIndex = this.navLinks.indexOf(event.currentTarget);
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.navLinks.length) % this.navLinks.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.navLinks.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.navLinks.length - 1;
        break;
      case 'Enter':
      case ' ':
        this.setActive(currentIndex, { focus: false, toggle: true, keepVisible: true });
        event.preventDefault();
        return;
      default:
        return;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      this.setActive(nextIndex, { keepVisible: true });
    }
  }

  setInitialActive() {
    const preselectedIndex = this.navLinks.findIndex(
      (link) =>
        link.classList.contains('tabs-navigation-li-active') ||
        link.classList.contains('smart-tabs-navigation-li-active')
    );
    const indexToActivate = preselectedIndex >= 0 ? preselectedIndex : 0;
    this.setActive(indexToActivate, { focus: false, force: true });
  }

  isAccordionMode() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  setActive(index, { focus = true, toggle = false, force = false, keepVisible = false } = {}) {
    const currentlyActiveIndex = this.navLinks.findIndex((link) => link.classList.contains('tabs-navigation-li-active'));
    const isSameTab = currentlyActiveIndex === index;

    if (toggle && isSameTab && this.isAccordionMode() && !force) {
      this.deactivateTab(index);
      if (this.navLinks[index]) {
        this.navLinks[index].setAttribute('tabindex', '0');
      }
      return;
    }

    this.navLinks.forEach((link, linkIndex) => {
      const shouldActivate = linkIndex === index;
      if (shouldActivate) {
        this.activateTab(linkIndex);
      } else {
        this.deactivateTab(linkIndex);
      }
    });

    if (focus && this.navLinks[index]) {
      this.navLinks[index].focus();
    }

    if (keepVisible) {
      this.keepNavigationVisible();
    }
  }

  activateTab(index) {
    const link = this.navLinks[index];
    const panel = this.panels[index];

    if (!link || !panel) return;

    link.classList.add('tabs-navigation-li-active');
    link.classList.add('smart-tabs-navigation-li-active');
    link.setAttribute('aria-selected', 'true');
    link.setAttribute('aria-expanded', 'true');
    link.setAttribute('tabindex', '0');
    panel.classList.add('tabs-content-block-active');
    panel.classList.add('smart-tabs-content-block-active');
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', 'false');
  }

  deactivateTab(index) {
    const link = this.navLinks[index];
    const panel = this.panels[index];

    if (!link || !panel) return;

    link.classList.remove('tabs-navigation-li-active');
    link.classList.remove('smart-tabs-navigation-li-active');
    link.setAttribute('aria-selected', 'false');
    link.setAttribute('aria-expanded', 'false');
    link.setAttribute('tabindex', '-1');
    panel.classList.remove('tabs-content-block-active');
    panel.classList.remove('smart-tabs-content-block-active');
    panel.setAttribute('hidden', 'hidden');
    panel.setAttribute('aria-hidden', 'true');
  }

  cleanEmptyParagraphs() {
    const candidates = this.wrapper.querySelectorAll('.tab-accordion__content p, .metafield-rich_text_field p');
    candidates.forEach((paragraph) => {
      const normalizedText = paragraph.textContent.replace(/\u00A0/g, '').trim();
      const hasMedia = paragraph.querySelector('img, video, iframe, table, svg, picture');

      if (!normalizedText && !hasMedia) {
        paragraph.classList.add('tabs-empty-paragraph');
      }
    });
  }

  keepNavigationVisible() {
    const ensureVisible = () => {
      const rect = this.navigation.getBoundingClientRect();
      const viewportPadding = 16;
      const lowerThreshold = Math.min(window.innerHeight * 0.5, window.innerHeight - 120);
      const isOutOfView = rect.top < viewportPadding || rect.top > lowerThreshold;
      if (!isOutOfView) {
        return;
      }

      const targetTop = window.scrollY + rect.top - viewportPadding;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };

    ensureVisible();
    window.requestAnimationFrame(ensureVisible);
    window.setTimeout(ensureVisible, 220);
  }

  destroy() {
    this.detachEvents();
  }
}

const initTabs = (scope) => {
  const wrappers = scope.querySelectorAll('.tabs-wrapper--custom');
  wrappers.forEach((wrapper) => {
    if (tabsState.registry.has(wrapper)) {
      return;
    }

    const instance = new Tabs(wrapper);
    tabsState.registry.set(wrapper, instance);
  });
};

const destroyTabs = (scope) => {
  const wrappers = scope.querySelectorAll('.tabs-wrapper--custom');
  wrappers.forEach((wrapper) => {
    const instance = tabsState.registry.get(wrapper);
    if (instance) {
      instance.destroy();
      tabsState.registry.delete(wrapper);
    }
  });
};

const bootTabs = () => initTabs(document);

if (!tabsState.bootstrapped) {
  tabsState.bootstrapped = true;

  if (document.readyState !== 'loading') {
    bootTabs();
  } else {
    document.addEventListener('DOMContentLoaded', bootTabs, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    initTabs(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyTabs(event.target);
  });
} else {
  initTabs(document);
}
